import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldAlert, Mail, RefreshCw } from "lucide-react";
import { api, StorageKeys } from "../lib/api";
import { storage } from "../lib/storage";
import { useI18n } from "../lib/i18n";
import { GurSetuWordmark } from "./GurSetuWordmark";

/* ═══════════════════════════════════════════════════════════
   SUPPORT EMAIL — yahan change karo agar email badalna ho
═══════════════════════════════════════════════════════════ */
export const SUPPORT_EMAIL = "support@gursetu.app";

const COPY = {
  en: {
    title: "Account Suspended",
    sub: "Your GurSetu account has been suspended by the administration.",
    reason: "Reason",
    suspendedOn: "Suspended on",
    contactSub: "If you believe this is a mistake, please contact us —",
    contact: "Contact Support",
    recheck: "Re-check Status",
    checking: "Checking…",
  },
  hi: {
    title: "अकाउंट निलंबित",
    sub: "आपका GurSetu अकाउंट प्रशासन द्वारा निलंबित कर दिया गया है।",
    reason: "कारण",
    suspendedOn: "निलंबित तिथि",
    contactSub: "यदि आपको लगता है कि यह गलती है, तो कृपया संपर्क करें —",
    contact: "सपोर्ट से संपर्क करें",
    recheck: "स्थिति दोबारा जांचें",
    checking: "जांच हो रही है…",
  },
  pa: {
    title: "ਖਾਤਾ ਮੁਅੱਤਲ",
    sub: "ਤੁਹਾਡਾ GurSetu ਖਾਤਾ ਪ੍ਰਸ਼ਾਸਨ ਦੁਆਰਾ ਮੁਅੱਤਲ ਕਰ ਦਿੱਤਾ ਗਿਆ ਹੈ।",
    reason: "ਕਾਰਨ",
    suspendedOn: "ਮੁਅੱਤਲ ਮਿਤੀ",
    contactSub: "ਜੇਕਰ ਤੁਹਾਨੂੰ ਲੱਗਦਾ ਹੈ ਕਿ ਇਹ ਗਲਤੀ ਹੈ, ਤਾਂ ਕਿਰਪਾ ਕਰਕੇ ਸੰਪਰਕ ਕਰੋ —",
    contact: "ਸਪੋਰਟ ਨਾਲ ਸੰਪਰਕ ਕਰੋ",
    recheck: "ਦੁਬਾਰਾ ਜਾਂਚ ਕਰੋ",
    checking: "ਜਾਂਚ ਹੋ ਰਹੀ ਹੈ…",
  },
};

export default function SuspensionGuard() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;

  const [suspended, setSuspended] = useState(null); // null | false | {reason,date}
  const [checking, setChecking] = useState(false);

  const check = useCallback(async (manual = false) => {
    const userId = storage.get(StorageKeys.UserId, "");
    if (!userId) return; // registered nahi hai → guard kuch nahi karega
    if (manual) setChecking(true);
    try {
      const s = await api.get(`/sangat/me/${userId}`);
      if (s && s.isSuspended) {
        setSuspended({
          reason: s.suspensionReason || "",
          date: s.suspendedAt || null,
        });
      } else {
        setSuspended(false);
      }
    } catch (e) {
      // network error — ignore, do nothing
    } finally {
      if (manual) setChecking(false);
    }
  }, []);

  // Mount pe check + har 60s me re-check + window focus pe re-check
  useEffect(() => {
    check();
    const iv = setInterval(() => check(false), 60000);
    const onFocus = () => check(false);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, [check]);

  return createPortal(
    <AnimatePresence>
      {suspended && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[2147483647] flex items-center justify-center p-4 bg-gradient-to-b from-[#f7f4ed] via-white to-[#f3efe6] dark:from-[#080b10] dark:via-[#0b1018] dark:to-[#0d121a]"
        >
          <motion.div
            initial={{ scale: 0.92, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            className="w-full max-w-md rounded-[28px] border border-red-300/60 dark:border-red-500/30 bg-white dark:bg-[#101722] shadow-2xl p-6 text-center relative overflow-hidden"
          >
            {/* Top golden glow */}
            <div className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 w-56 h-40 bg-amber-400/15 rounded-full blur-3xl" />

            {/* GurSetu animated wordmark */}
            <div className="flex justify-center mb-3 relative">
              <GurSetuWordmark size="sm" />
            </div>

            {/* Shield icon */}
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.15, type: "spring", damping: 15 }}
              className="mx-auto w-16 h-16 rounded-full bg-red-500/15 border border-red-500/40 grid place-items-center mb-4"
            >
              <ShieldAlert size={30} className="text-red-500 dark:text-red-400" />
            </motion.div>

            <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white">
              {c.title}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">
              {c.sub}
            </p>

            {suspended.reason && (
              <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-left">
                <p className="text-xs font-bold text-red-600 dark:text-red-400 uppercase tracking-wider mb-1">
                  {c.reason}
                </p>
                <p className="text-sm text-slate-700 dark:text-slate-300">{suspended.reason}</p>
              </div>
            )}

            {suspended.date && (
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-3">
                {c.suspendedOn}: {new Date(suspended.date).toLocaleString()}
              </p>
            )}

            {/* Contact support */}
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-5">
              {c.contactSub}
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("GurSetu Account Suspension Appeal")}`}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 text-black font-extrabold shadow-lg shadow-amber-500/20 hover:scale-[1.02] transition-transform"
            >
              <Mail size={16} />
              {c.contact} · {SUPPORT_EMAIL}
            </a>

            <button
              onClick={() => check(true)}
              disabled={checking}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full border border-slate-300 dark:border-white/10 text-slate-600 dark:text-slate-300 font-semibold hover:bg-slate-100 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={14} className={checking ? "animate-spin" : ""} />
              {checking ? c.checking : c.recheck}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}